import { useTranslation } from 'react-i18next'; 
import { useNavigate } from 'react-router-dom';
import { useShop } from '../../../shared/store';

interface CartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const CartDrawer = ({ isOpen, onClose }: CartDrawerProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { cart, cartSubtotal, currentUser, removeFromCart, updateCartQuantity } = useShop();
  const checkoutEditionStorageKey = 'haapa.checkout.edition';
  const checkoutPodProjectStorageKey = 'haapa.checkout.podProjectId';

  const deliveryFee = 0;
  const estimatedTotal = cartSubtotal + deliveryFee;

  return (
    <div
      className={`fixed inset-0 z-50 transition ${isOpen ? 'pointer-events-auto' : 'pointer-events-none'}`}
      aria-hidden={!isOpen}
    >
      {/* Overlay */}
      <button
        type="button"
        tabIndex={isOpen ? 0 : -1}
        onClick={onClose}
        className={`absolute inset-0 bg-black/50 transition-opacity ${isOpen ? 'opacity-100' : 'opacity-0'}`}
        aria-label={t('common.close')}
      />

      {/* Drawer */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={t('cart.title')}
        className={`absolute right-0 top-0 flex h-full w-full max-w-md flex-col bg-white shadow-2xl transition-transform duration-300 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <header className="flex items-center justify-between border-b border-orange-200 px-6 py-5">
          <h2 className="text-3xl font-semibold text-orange-600">
            {t('cart.title')} <span className="text-base font-normal text-orange-400">({cart.length})</span>
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-orange-200 px-3 py-1 text-sm font-semibold text-orange-500 transition hover:border-orange-300 hover:text-orange-600"
          >
            {t('common.close')}
          </button>
        </header>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {cart.length === 0 ? (
            <div className="space-y-4 rounded-2xl border border-orange-100 bg-orange-50 p-5 text-center">
              <p className="text-sm text-orange-500">{t('cart.emptySubtitle')}</p>
              <button
                type="button"
                onClick={() => {
                  onClose();
                  void navigate('/products');
                }}
                className="rounded-xl bg-orange-400 px-4 py-2 text-sm font-semibold text-white transition hover:bg-orange-500"
              >
                {t('cart.browseProducts')}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {cart.map((cartItem) => (
                <article key={cartItem.product.id} className="rounded-2xl border border-orange-100 bg-white p-3 shadow-sm">
                  <div className="flex gap-3">
                    <img
                      src={cartItem.product.imageUrl}
                      alt={t(cartItem.product.nameKey)}
                      className="h-16 w-20 rounded-lg object-cover"
                    />
                    <div className="flex-1">
                      <h3 className="line-clamp-1 text-base font-semibold text-orange-600">{t(cartItem.product.nameKey)}</h3>
                      <p className="text-sm text-orange-400">$ {cartItem.product.price.toFixed(2)}</p>
                      <div className="mt-2 flex items-center justify-between gap-3">
                        <div className="inline-flex items-center rounded-lg border border-orange-200">
                          <button
                            type="button"
                            onClick={() =>
                              updateCartQuantity(cartItem.product.id, Math.max(1, cartItem.quantity - 1))
                            }
                            className="px-2 py-1 text-sm font-semibold text-orange-500 transition hover:bg-orange-50"
                            aria-label={t('cart.decreaseQuantity')}
                          >
                            -
                          </button>
                          <span className="min-w-8 px-2 text-center text-sm font-semibold text-orange-600">
                            {cartItem.quantity}
                          </span>
                          <button
                            type="button"
                            onClick={() => updateCartQuantity(cartItem.product.id, cartItem.quantity + 1)}
                            className="px-2 py-1 text-sm font-semibold text-orange-500 transition hover:bg-orange-50"
                            aria-label={t('cart.increaseQuantity')}
                          >
                            +
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFromCart(cartItem.product.id)}
                          className="text-xs font-semibold text-orange-500 transition hover:text-orange-600"
                        >
                          {t('cart.remove')}
                        </button>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <footer className="space-y-4 border-t border-orange-200 px-6 py-5">
          <div className="flex items-center justify-between">
            <p className="text-2xl font-semibold text-orange-600">{t('cart.estimatedTotal')}</p>
            <p className="text-2xl font-semibold text-orange-700">$ {estimatedTotal.toFixed(2)}</p>
          </div>
          <p className="text-sm text-orange-400">{t('cart.taxesNote')}</p>

          <button
            type="button"
            onClick={() => {
              onClose();
              if (!currentUser) {
                void navigate('/auth');
                return;
              }

              const edition = window.sessionStorage.getItem(checkoutEditionStorageKey) as 'digital' | 'print' | null;
              const podProjectId = window.sessionStorage.getItem(checkoutPodProjectStorageKey) ?? undefined;
              void navigate('/checkout', {
                state: edition || podProjectId ? { ...(edition ? { edition } : {}), ...(podProjectId ? { podProjectId } : {}) } : undefined,
              });
            }}
            className="w-full rounded-xl bg-orange-500 px-4 py-3 text-sm font-semibold text-white transition hover:bg-orange-600"
          >
            {t('cart.proceedCheckout')}
          </button>

          <button
            type="button"
            onClick={() => {
              onClose();
              void navigate('/cart');
            }}
            className="w-full rounded-xl border border-orange-200 px-4 py-3 text-sm font-semibold text-orange-500 transition hover:border-orange-300 hover:text-orange-600"
          >
            {t('cart.viewCart')}
          </button>

          <p className="text-center text-sm font-semibold text-orange-600">{t('cart.secureCheckout')}</p>
        </footer>
      </aside>
    </div>
  );
};

export default CartDrawer;
